import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';
import { motion, AnimatePresence } from 'framer-motion';
import PositionAnalysis from './PositionAnalysis';
import { calculateRealTA } from '../utils/technicalAnalysis';
import { fetchOHLCV, fetchTokenData } from '../utils/tokenApi';

const { FiExternalLink, FiBarChart2, FiSettings, FiSave, FiX, FiXCircle, FiAlertCircle, FiLayers, FiTrash2, FiClock, FiCpu, FiUser, FiLoader } = FiIcons;

const SmallToggle = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={cn(
      "relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none",
      enabled ? "bg-blue-500" : "bg-slate-700"
    )}
  >
    <span
      className={cn(
        "absolute h-2 w-2 transform rounded-full bg-white transition-transform",
        enabled ? "translate-x-4" : "translate-x-1"
      )}
    />
  </button>
);

export default function PositionItem({ position, onManualSell, onUpdatePosition, onDeletePosition, onForceClose, botTimeframe = '15m' }) {
  const [isSelling, setIsSelling] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [customPct, setCustomPct] = useState('');
  const [isLoadingATR, setIsLoadingATR] = useState(false);

  const { id, tokenAddress, pairAddress, tokenName, tokenSymbol, entryPrice, currentPrice, highestPrice, hardStop, takeProfit, trailingStop, tpTiers, status, sellReason, dexUrl, amount, mode, error, limitPrice, botTriggered, txids, boughtAt, closedAt, timestamp, updatedAt } = position;
  const lastTxid = txids && txids.length > 0 ? txids[txids.length - 1] : null;
  const fmtTs = (v) => { try { return v ? new Date(v).toLocaleString() : null; } catch { return null; } };
  const boughtTs = fmtTs(boughtAt) || fmtTs(timestamp);
  const soldTs = status === 'CLOSED' ? (fmtTs(closedAt) || fmtTs(updatedAt)) : null;

  // Local state for editing parameters
  const [editHardStop, setEditHardStop] = useState(hardStop || '');
  const [editHardStopEnabled, setEditHardStopEnabled] = useState(hardStop !== null && hardStop !== undefined);
  
  const [editTakeProfit, setEditTakeProfit] = useState(takeProfit || '');
  const [editTakeProfitEnabled, setEditTakeProfitEnabled] = useState(takeProfit !== null && takeProfit !== undefined);
  
  const [editTrailingStop, setEditTrailingStop] = useState(trailingStop || '');
  const [editTrailingStopEnabled, setEditTrailingStopEnabled] = useState(trailingStop !== null && trailingStop !== undefined);

  const defaultTiers = [
    { gain: 50, amount: 25, enabled: true, triggered: false },
    { gain: 100, amount: 25, enabled: true, triggered: false },
    { gain: 200, amount: 50, enabled: true, triggered: false }
  ];
  
  const [editTpTiers, setEditTpTiers] = useState(tpTiers || defaultTiers);

  // NOTE: the edit form is intentionally NOT kept in sync with props on every render.
  // Positions refetch every 2s (new object refs, incl. a fresh tpTiers array), so a
  // prop->state effect here would clobber in-progress edits every tick. Instead we seed
  // the fields when the settings panel is opened (toggleSettings / handleApplyATR).

  const isPending = status === 'PENDING';
  const isExecutingBuy = status === 'EXECUTING_BUY';
  const isClosing = status === 'CLOSING';

  const displayEntry = isPending || isExecutingBuy ? limitPrice : entryPrice;
  const pnlPercent = isPending || isExecutingBuy
    ? ((currentPrice - limitPrice) / limitPrice) * 100
    : ((currentPrice - entryPrice) / entryPrice) * 100;
  
  const isPositive = pnlPercent >= 0;
  const isActive = status === 'ACTIVE' || status === 'ERROR' || isClosing || isPending || isExecutingBuy;

  const formatPrice = (price) => {
    if (!price) return '0.00';
    if (price < 0.00001) return price.toFixed(8);
    if (price < 0.01) return price.toFixed(6);
    return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  const handleSellSubmit = async (pct) => {
    const val = parseFloat(pct);
    if (isNaN(val) || val <= 0 || val > 100) return;
    setIsSelling(false);
    setCustomPct('');
    try {
      await onManualSell(id, val);
    } catch (e) {
      console.error("Sell interaction emitted error:", e);
    }
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...editTpTiers];
    newTiers[index][field] = value;
    setEditTpTiers(newTiers);
  };

  const handleApplyATR = (suggestions) => {
    setEditHardStop(suggestions.hardStop.pct.toFixed(2));
    setEditHardStopEnabled(true);
    setEditTakeProfit(suggestions.takeProfit.pct.toFixed(2));
    // Don't silently stack two exit mechanisms: if tiered take-profits are already
    // active, leave the single take-profit off and let the tiers scale out.
    const hasActiveTiers = editTpTiers.some(t => t.enabled);
    setEditTakeProfitEnabled(!hasActiveTiers);
    setEditTrailingStop(suggestions.trailingStop.pct.toFixed(2));
    setEditTrailingStopEnabled(true);

    // Close analysis and open settings to let user verify/save
    setShowAnalysis(false);
    setShowSettings(true);
  };

  const handleLoadATR = async () => {
    setIsLoadingATR(true);
    try {
      let activePair = pairAddress;
      if (!activePair) {
        const freshData = await fetchTokenData(tokenAddress);
        activePair = freshData.pairAddress;
      }
      if (activePair) {
        const ohlcv = await fetchOHLCV(activePair, botTimeframe);
        const ta = calculateRealTA(ohlcv, currentPrice, entryPrice);
        handleApplyATR(ta.suggestions);
      }
    } catch (err) {
      console.error("Failed to load ATR suggestions", err);
    } finally {
      setIsLoadingATR(false);
    }
  };

  const handleSaveSettings = () => {
    if (onUpdatePosition) {
      onUpdatePosition(id, {
        hardStop: editHardStopEnabled && editHardStop ? parseFloat(editHardStop) : null,
        takeProfit: editTakeProfitEnabled && editTakeProfit ? parseFloat(editTakeProfit) : null,
        trailingStop: editTrailingStopEnabled && editTrailingStop ? parseFloat(editTrailingStop) : null,
        tpTiers: editTpTiers.map(t => ({
          ...t,
          gain: parseFloat(t.gain) || 0,
          amount: parseFloat(t.amount) || 0
        }))
      });
    }
    setShowSettings(false);
  };

  const toggleSettings = () => {
    const opening = !showSettings;
    if (opening) {
      // Seed the edit fields from the latest saved values at open time only.
      setEditHardStop(hardStop || '');
      setEditHardStopEnabled(hardStop !== null && hardStop !== undefined);
      setEditTakeProfit(takeProfit || '');
      setEditTakeProfitEnabled(takeProfit !== null && takeProfit !== undefined);
      setEditTrailingStop(trailingStop || '');
      setEditTrailingStopEnabled(trailingStop !== null && trailingStop !== undefined);
      setEditTpTiers(tpTiers ? JSON.parse(JSON.stringify(tpTiers)) : defaultTiers);
    }
    setShowSettings(opening);
    setShowAnalysis(false);
    setIsSelling(false);
  };

  const toggleAnalysis = () => {
    setShowAnalysis(!showAnalysis);
    setShowSettings(false);
    setIsSelling(false);
  };

  const toggleSelling = () => {
    setIsSelling(!isSelling);
    setShowAnalysis(false);
    setShowSettings(false);
  };

  return (
    <motion.div 
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={cn(
        "p-4 rounded-xl border transition-all duration-300",
        isActive ? (status === 'ERROR' ? "bg-[#2E1A1A] border-red-700/50" : "bg-[#1A1F2E] border-slate-700/50") : "bg-[#0B0E14] border-slate-800 opacity-60"
      )}
    >
      <div className="flex flex-col lg:flex-row justify-between gap-4">
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-white">{tokenName || 'Unknown'}</span>
                <span className="text-xs text-slate-500 font-mono">
                  ({tokenSymbol}) {amount ? `• ${amount.toFixed(3)} SOL` : ''}
                </span>

                {/* Auto / Manual Origin Label */}
                <span className={cn(
                  "text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider flex items-center gap-1",
                  botTriggered ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-slate-800 text-slate-400 border border-slate-700"
                )}>
                  <SafeIcon icon={botTriggered ? FiCpu : FiUser} />
                  {botTriggered ? 'AUTO' : 'MANUAL'}
                </span>

                {mode === 'LIVE' && (
                  <span className="text-[9px] bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">LIVE</span>
                )}
                {dexUrl && (
                  <a href={dexUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                    <SafeIcon icon={FiExternalLink} className="text-xs" />
                  </a>
                )}
                {lastTxid && (
                  <a
                    href={`https://solscan.io/tx/${lastTxid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[9px] text-slate-500 hover:text-slate-300 font-mono"
                    title="View latest transaction on Solscan"
                  >
                    tx↗
                  </a>
                )}
              </div>
              <div className="font-mono text-[10px] text-slate-500 mt-1">
                {tokenAddress.substring(0, 6)}...{tokenAddress.substring(tokenAddress.length - 4)}
              </div>
              <div className="text-[9px] text-slate-500 mt-1 space-x-2">
                {boughtTs && <span>Bought: <span className="text-slate-400">{boughtTs}</span></span>}
                {soldTs && <span>Sold: <span className="text-slate-400">{soldTs}</span></span>}
              </div>
            </div>

            <div className={cn(
              "text-[10px] px-2 py-0.5 rounded uppercase font-bold tracking-widest flex items-center gap-1.5",
              isClosing ? "bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 animate-pulse" :
              isExecutingBuy ? "bg-blue-500/10 text-blue-400 border border-blue-500/20 animate-pulse" :
              isPending ? "bg-purple-500/10 text-purple-400 border border-purple-500/20" :
              status === 'ERROR' ? "bg-red-500/10 text-red-500 border border-red-500/20" :
              isActive ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-slate-800 text-slate-400"
            )}>
              {isPending && <SafeIcon icon={FiClock} className="text-xs" />}
              {status}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] text-slate-500 uppercase font-bold mb-0.5">{isPending || isExecutingBuy ? 'Target' : 'Entry'}</div>
              <div className="font-mono text-sm">${formatPrice(displayEntry)}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase font-bold mb-0.5">Live</div>
              <div className={cn("font-mono text-sm font-bold", isPending || isExecutingBuy ? "text-slate-200" : (isPositive ? "text-green-400" : "text-red-400"))}>
                ${formatPrice(currentPrice)}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 bg-black/20 rounded-lg p-3 border border-slate-800/50 space-y-2 flex flex-col justify-center min-w-[200px]">
          {isPending || isExecutingBuy ? (
             <div className="text-center text-slate-400 text-xs font-medium space-y-1">
               <SafeIcon icon={FiClock} className="text-purple-400 text-lg mx-auto mb-1" />
               <p>Waiting for price condition...</p>
               <p className="text-[10px] text-slate-500">Protection settings will apply on fill.</p>
             </div>
          ) : (
            <>
              {hardStop !== null && hardStop !== undefined && (
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500 uppercase font-bold">Hard Stop:</span>
                  <span className="font-mono text-slate-300">${formatPrice(entryPrice * (1 - hardStop / 100))}<span className="text-slate-500"> (-{parseFloat(hardStop.toFixed(1))}%)</span></span>
                </div>
              )}
              {takeProfit !== null && takeProfit !== undefined && (
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500 uppercase font-bold">Take Profit:</span>
                  <span className="font-mono text-slate-300">${formatPrice(entryPrice * (1 + takeProfit / 100))}<span className="text-slate-500"> (+{parseFloat(takeProfit.toFixed(1))}%)</span></span>
                </div>
              )}
              {trailingStop !== null && trailingStop !== undefined && (
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500 uppercase font-bold">Trailing:</span>
                  <span className="font-mono text-slate-300">${formatPrice(highestPrice * (1 - trailingStop / 100))}<span className="text-slate-500"> (-{parseFloat(trailingStop.toFixed(1))}%)</span></span>
                </div>
              )}
              {highestPrice != null && (
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500 uppercase font-bold">Peak:</span>
                  <span className="font-mono text-emerald-300/90">${formatPrice(highestPrice)}{entryPrice ? <span className="text-emerald-300/60"> (+{parseFloat((((highestPrice / entryPrice) - 1) * 100).toFixed(1))}%)</span> : null}</span>
                </div>
              )}
              {tpTiers && tpTiers.some(t => t.enabled) && (
                <div className="flex justify-between text-[10px] mt-1 pt-1 border-t border-slate-700/50">
                  <span className="text-slate-500 uppercase font-bold">Active Tiers:</span>
                  <span className="font-mono text-blue-400">
                    {tpTiers.filter(t => t.enabled && !t.triggered).length} / {tpTiers.filter(t => t.enabled).length} Pending
                  </span>
                </div>
              )}
              {!isActive && (
                <div className="text-[10px] text-orange-400 font-bold uppercase mt-1">
                  Trigger: {sellReason}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between lg:justify-center gap-3 min-w-[120px]">
          <div className="text-right">
            <div className="text-[10px] text-slate-500 uppercase font-bold">{isPending || isExecutingBuy ? 'Distance' : 'PnL'}</div>
            <div className={cn("flex items-center gap-1 text-lg font-black", (isPending || isExecutingBuy) ? "text-slate-300" : (isPositive ? "text-green-400" : "text-red-400"))}>
              {pnlPercent > 0 ? '+' : ''}{pnlPercent.toFixed(2)}%
            </div>
          </div>
          
          {isActive && (
            <div className="flex gap-2">
              {isPending || isExecutingBuy ? (
                <button
                  onClick={() => onDeletePosition(id)}
                  disabled={isExecutingBuy}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-slate-700 hover:border-red-500/30 rounded-lg text-xs font-bold uppercase transition-colors disabled:opacity-50"
                  title="Cancel Pending Limit Order"
                >
                  {isExecutingBuy ? 'Executing...' : 'Cancel Order'}
                </button>
              ) : (
                <>
                  <button
                    onClick={toggleSettings}
                    disabled={isClosing}
                    className={cn(
                      "px-2.5 py-1.5 border rounded-lg text-xs font-bold transition-colors flex items-center gap-1",
                      showSettings ? "bg-blue-500/20 text-blue-400 border-blue-500/30" : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700 hover:text-white disabled:opacity-50"
                    )}
                    title="Adjust Risk Parameters"
                  >
                    <SafeIcon icon={FiSettings} />
                  </button>
                  <button
                    onClick={toggleAnalysis}
                    className={cn(
                      "px-2.5 py-1.5 border rounded-lg text-xs font-bold transition-colors flex items-center gap-1",
                      showAnalysis ? "bg-purple-500/20 text-purple-400 border-purple-500/30" : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700 hover:text-white"
                    )}
                    title="AI Strategy Analysis"
                  >
                    <SafeIcon icon={FiBarChart2} />
                  </button>
                  {!isSelling ? (
                    <button
                      onClick={toggleSelling}
                      disabled={isClosing}
                      className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 rounded-lg text-xs font-bold uppercase transition-colors disabled:opacity-50"
                    >
                      {isClosing ? '...' : (status === 'ERROR' ? 'Retry Sell' : 'Sell')}
                    </button>
                  ) : (
                    <button
                      onClick={toggleSelling}
                      className="px-3 py-1.5 bg-slate-500/10 hover:bg-slate-500/20 text-slate-400 border border-slate-500/20 rounded-lg text-xs font-bold uppercase transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  {onForceClose && (
                    <button
                      onClick={() => { if (window.confirm('Mark this position closed without selling? Use only if you have already exited the token (e.g. swapped it in the Wallet).')) onForceClose(id); }}
                      title="Force close — marks the position closed without an on-chain sell"
                      className="px-2.5 py-1.5 bg-slate-800 hover:bg-yellow-500/20 text-slate-400 hover:text-yellow-400 border border-slate-700 hover:border-yellow-500/30 rounded-lg text-xs font-bold uppercase transition-colors"
                    >
                      <SafeIcon icon={FiXCircle} />
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {status === 'CLOSED' && onDeletePosition && (
            <div className="flex gap-2">
              <button
                onClick={() => onDeletePosition(id)}
                className="px-3 py-1.5 bg-slate-800 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-slate-700 hover:border-red-500/30 rounded-lg text-xs font-bold uppercase transition-colors flex items-center gap-1.5"
                title="Remove Position"
              >
                <SafeIcon icon={FiTrash2} /> Remove
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Global Error Banner */}
      {error && (
        <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-2.5 text-xs text-red-400 font-bold flex items-center gap-2">
          <SafeIcon icon={FiAlertCircle} className="text-sm flex-shrink-0" />
          <span>Execution Error: {error}. Adjust slippage or retry manually.</span>
        </div>
      )}

      {/* Expandable Parameter Settings Panel */}
      <AnimatePresence>
        {showSettings && isActive && !isClosing && !isPending && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-700/50 mt-4 pt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-wider flex items-center gap-2">
                  <SafeIcon icon={FiSettings} /> Adjust Risk Parameters
                </h4>
                {!botTriggered && (
                  <button
                    onClick={handleLoadATR}
                    disabled={isLoadingATR}
                    className="text-[9px] font-bold uppercase tracking-wider bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20 px-2 py-1 rounded flex items-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    {isLoadingATR ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCpu} />}
                    Use ATR Settings
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className={cn("space-y-1.5 transition-opacity duration-200", !editHardStopEnabled && "opacity-50")}>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Hard Stop (%)</label>
                    <SmallToggle enabled={editHardStopEnabled} onChange={() => setEditHardStopEnabled(!editHardStopEnabled)} />
                  </div>
                  <input
                    type="number"
                    value={editHardStop}
                    onChange={e => setEditHardStop(e.target.value)}
                    placeholder="e.g. 10"
                    className="input-field py-2 text-sm"
                    disabled={!editHardStopEnabled}
                  />
                </div>
                <div className={cn("space-y-1.5 transition-opacity duration-200", !editTakeProfitEnabled && "opacity-50")}>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Take Profit (%)</label>
                    <SmallToggle enabled={editTakeProfitEnabled} onChange={() => setEditTakeProfitEnabled(!editTakeProfitEnabled)} />
                  </div>
                  <input
                    type="number"
                    value={editTakeProfit}
                    onChange={e => setEditTakeProfit(e.target.value)}
                    placeholder="e.g. 50"
                    className="input-field py-2 text-sm"
                    disabled={!editTakeProfitEnabled}
                  />
                </div>
                <div className={cn("space-y-1.5 transition-opacity duration-200", !editTrailingStopEnabled && "opacity-50")}>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] text-slate-400 font-bold uppercase">Trailing Stop (%)</label>
                    <SmallToggle enabled={editTrailingStopEnabled} onChange={() => setEditTrailingStopEnabled(!editTrailingStopEnabled)} />
                  </div>
                  <input
                    type="number"
                    value={editTrailingStop}
                    onChange={e => setEditTrailingStop(e.target.value)}
                    placeholder="e.g. 15"
                    className="input-field py-2 text-sm"
                    disabled={!editTrailingStopEnabled}
                  />
                </div>
              </div>

              {/* Tiered Take Profit Section */}
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <h5 className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <SafeIcon icon={FiLayers} /> Tiered Take Profits
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  {editTpTiers.map((tier, idx) => (
                    <div 
                      key={idx} 
                      className={cn(
                        "bg-[#0B0E14] p-3 rounded-lg border border-slate-800 transition-opacity duration-200",
                        !tier.enabled && "opacity-50",
                        tier.triggered && "opacity-50 grayscale border-green-500/20"
                      )}
                    >
                      <div className="flex items-center justify-between mb-3 border-b border-slate-800/50 pb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">
                          Tier {idx + 1} {tier.triggered && <span className="text-green-400 ml-1">(Hit)</span>}
                        </span>
                        <SmallToggle 
                          enabled={tier.enabled} 
                          onChange={() => !tier.triggered && handleTierChange(idx, 'enabled', !tier.enabled)} 
                        />
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-1 space-y-1">
                          <label className="text-[9px] text-slate-500 uppercase font-bold">Gain %</label>
                          <input
                            type="number"
                            value={tier.gain}
                            onChange={e => handleTierChange(idx, 'gain', e.target.value)}
                            className="input-field py-1.5 px-2 text-xs"
                            disabled={!tier.enabled || tier.triggered}
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <label className="text-[9px] text-slate-500 uppercase font-bold">Sell %</label>
                          <input
                            type="number"
                            value={tier.amount}
                            onChange={e => handleTierChange(idx, 'amount', e.target.value)}
                            className="input-field py-1.5 px-2 text-xs"
                            disabled={!tier.enabled || tier.triggered}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={toggleSettings}
                  className="px-4 py-2 border border-slate-700 text-slate-400 rounded-lg text-xs font-bold hover:bg-slate-800 transition-colors flex items-center gap-1.5"
                >
                  <SafeIcon icon={FiX} /> Cancel
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-lg shadow-blue-500/20"
                >
                  <SafeIcon icon={FiSave} /> Save Overrides
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expandable Strategy Analysis Panel */}
      <AnimatePresence>
        {showAnalysis && isActive && !isPending && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <PositionAnalysis position={position} onApplyATR={handleApplyATR} botTimeframe={botTimeframe} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expandable Sell Panel */}
      <AnimatePresence>
        {isSelling && !isClosing && !isPending && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-700/50 mt-4 pt-4 flex flex-col lg:flex-row items-center gap-4">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap hidden lg:block">
                Close Amount
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 w-full lg:w-auto">
                {[25, 50, 75, 100].map(pct => (
                  <button
                    key={pct}
                    onClick={() => handleSellSubmit(pct)}
                    className="px-4 py-1.5 bg-[#0B0E14] hover:bg-slate-800 border border-slate-700 rounded-lg text-xs font-bold text-white transition-colors flex-1 lg:flex-none"
                  >
                    {pct}%
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 w-full lg:w-auto lg:ml-auto">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={customPct}
                  onChange={(e) => setCustomPct(e.target.value)}
                  placeholder="Custom %"
                  className="input-field py-1.5 px-3 text-xs w-full lg:w-24 min-h-0 text-center lg:text-left"
                />
                <button
                  onClick={() => handleSellSubmit(customPct)}
                  className="px-6 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 rounded-lg text-xs font-bold uppercase transition-colors whitespace-nowrap"
                >
                  Execute
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}